import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, BlockStack, Text } from "@shopify/polaris";
import DepartmentMultiSelect from "components/department-multi-select/department-multi-select";
import { updateOnboardingDepartments } from "services/onboarding.service";
import { fetchAssignableDepartments } from "services/departments.service";
import { fetchOnboardingDetails } from "containers/onboarding-view-container/onboarding-container.slice";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";

interface DepartmentOption {
  id: string;
  name: string;
}

interface OnboardingDepartmentModalProps {
  companyId: string;
  onboardingId: string;
  currentDepartments?: DepartmentOption[];
}

/**
 * modalX content for updating the department(s) assigned to an onboarding.
 * Master-only (opened from the onboarding view's departments card); the
 * backend takes the selection verbatim.
 */
const OnboardingDepartmentModal: React.FC<OnboardingDepartmentModalProps> = ({
  companyId,
  onboardingId,
  currentDepartments = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [selectedIds, setSelectedIds] = useState<string[]>(
    currentDepartments.map((d) => d.id)
  );
  const [assignable, setAssignable] = useState<DepartmentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const data = await fetchAssignableDepartments(companyId);
        if (!cancelled) setAssignable(data.departments || []);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Show every department already on the record as a tag, even ones outside the
  // editor's assignable set (union), so nothing renders invisibly.
  const options = useMemo(() => {
    const byId = new Map<string, DepartmentOption>();
    [...assignable, ...currentDepartments].forEach((d) => {
      if (d && d.id) byId.set(d.id, { id: d.id, name: d.name });
    });
    return Array.from(byId.values());
  }, [assignable, currentDepartments]);

  const close = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleSave = async () => {
    if (!companyId || !onboardingId || loadError) return;

    setSubmitting(true);
    try {
      await updateOnboardingDepartments(companyId, onboardingId, selectedIds);
      dispatch(fetchOnboardingDetails({ companyId, onboardingId }));
      toast.success(t("departments_updated"));
      close();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error || e?.response?.data?.message || t("error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <BlockStack gap="400">
        {loadError ? (
          <Text as="p" tone="critical">
            {t("error")}
          </Text>
        ) : (
          <DepartmentMultiSelect
            companyId={companyId}
            value={selectedIds}
            onChange={setSelectedIds}
            disabled={loading || submitting}
            options={options}
          />
        )}
      </BlockStack>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <Button onClick={close} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={submitting}
          disabled={loading || loadError}
        >
          {t("save")}
        </Button>
      </div>
    </div>
  );
};

export default OnboardingDepartmentModal;
